// ===========================================
// Birthday helpers (frontend mirror, 2026-09-07)
// ===========================================
// Mirrors apps/backend/src/utils/birthdays.ts. `Student.birthDate` arrives as a
// `@db.Date` ISO string (UTC midnight), so every getter here is a UTC getter —
// the existing frontend `calculateAge` in OnboardingPage uses LOCAL getters and
// is wrong by a year on the birthday west of UTC.
//
// Used only for the teacher-dashboard cake icon, which reads `birthDate` from
// the lesson payload it already receives. The admin card and modal get their
// numbers from the server.

export function isPlaceholderBirthDate(birthDate: Date): boolean {
  return birthDate.getUTCMonth() === 0 && birthDate.getUTCDate() === 1;
}

export function ageTurningOn(birthDate: Date, onDay: Date): number {
  return onDay.getUTCFullYear() - birthDate.getUTCFullYear();
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** The dated birthday inside [from, to] (UTC midnight, inclusive), or null. */
export function birthdayFallsInWindow(birthDate: Date, from: Date, to: Date): Date | null {
  const birthMonth = birthDate.getUTCMonth();
  const birthDay = birthDate.getUTCDate();
  const isLeapDay = birthMonth === 1 && birthDay === 29;

  const cursor = new Date(from.getTime());
  while (cursor.getTime() <= to.getTime()) {
    const month = cursor.getUTCMonth();
    const day = cursor.getUTCDate();
    if (month === birthMonth && day === birthDay) return new Date(cursor.getTime());
    if (isLeapDay && month === 1 && day === 28 && !isLeapYear(cursor.getUTCFullYear())) {
      return new Date(cursor.getTime());
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return null;
}

/** Monday..Sunday (UTC midnight) of the week containing `day`. */
export function weekBoundsUTC(day: Date): { weekStart: Date; weekEnd: Date } {
  const weekStart = new Date(day.getTime());
  weekStart.setUTCDate(weekStart.getUTCDate() - ((weekStart.getUTCDay() + 6) % 7));
  const weekEnd = new Date(weekStart.getTime());
  weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
  return { weekStart, weekEnd };
}

export interface BirthdayThisWeek {
  /** `YYYY-MM-DD` of the birthday within the current week. */
  dateKey: string;
  ageTurning: number;
  isToday: boolean;
}

/**
 * Whether a student's birthday falls in the current (Mon–Sun) week, and the age
 * they turn. `todayKey` is the caller's notion of today as `YYYY-MM-DD` — the
 * dashboard already computes one, so the icon and the class list agree.
 * Returns null for a placeholder date or a birthday outside the week.
 */
export function birthdayThisWeek(
  birthDateIso: string | null | undefined,
  todayKey: string
): BirthdayThisWeek | null {
  if (!birthDateIso) return null;
  const birthDate = new Date(birthDateIso);
  if (Number.isNaN(birthDate.getTime())) return null;
  if (isPlaceholderBirthDate(birthDate)) return null;

  const today = new Date(`${todayKey}T00:00:00.000Z`);
  if (Number.isNaN(today.getTime())) return null;
  const { weekStart, weekEnd } = weekBoundsUTC(today);
  const birthday = birthdayFallsInWindow(birthDate, weekStart, weekEnd);
  if (!birthday) return null;

  const dateKey = birthday.toISOString().slice(0, 10);
  return { dateKey, ageTurning: ageTurningOn(birthDate, birthday), isToday: dateKey === todayKey };
}
